import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { api } from '../../services/api.js';
import { bufferLabel } from '../../utils/bufferMinutes.js';

/**
 * 386 slice 2 (#1278, FR-011, the approved mock-up): staff are WARNED, never refused, when a move, resize, create,
 * extension or car change leaves less than the car's gap next to another rental. One question to the server
 * (`GET /bookings/buffer-check`), one wording. A failed check shows nothing: a warning must never block a save.
 */

export interface BufferCheck {
  buffer_minutes: number;
  conflicts: Array<{ booking: string; side: 'before' | 'after'; gap_minutes: number }>;
}

/** The closest rental inside the gap, worded; null when the gap is kept (an overlap is refused elsewhere). */
export type GapWarningText = { title: string; detail: string };

export function gapWarningText(check: BufferCheck | null | undefined): GapWarningText | null {
  // Defensive: a stand-in or an older platform may answer with something else entirely.
  const close = (Array.isArray(check?.conflicts) ? check.conflicts : [])
    .filter((c) => c.gap_minutes >= 0)
    .sort((a, b) => a.gap_minutes - b.gap_minutes)[0];
  if (!close) return null;
  const where = close.side === 'before' ? 'after the rental before' : 'before the rental after';
  const title =
    close.gap_minutes === 0
      ? `No gap ${where} (…${close.booking}).`
      : `Only ${close.gap_minutes} minutes ${where} (…${close.booking}).`;
  return {
    title,
    detail: `This car's gap between rentals is ${bufferLabel(check?.buffer_minutes ?? 0).toLowerCase()}. You can still go ahead.`,
  };
}

export function useGapWarning(input: {
  carId: string | null | undefined;
  pickupAt: string | null | undefined;
  returnAt: string | null | undefined;
  bookingId?: string | null;
}) {
  const { carId, pickupAt, returnAt, bookingId } = input;
  const ready = Boolean(carId && pickupAt && returnAt);
  const params = new URLSearchParams({
    car_id: String(carId ?? ''),
    pickup_at: String(pickupAt ?? ''),
    return_at: String(returnAt ?? ''),
    ...(bookingId ? { booking_id: bookingId } : {}),
  });
  const query = useQuery({
    queryKey: ['buffer-check', carId, pickupAt, returnAt, bookingId ?? null],
    // A failed check is no warning, never an error state: the save it accompanies must not be held up by it.
    queryFn: () =>
      api
        .get<BufferCheck>(`/bookings/buffer-check?${params.toString()}`)
        .catch((): BufferCheck | null => null),
    enabled: ready,
    staleTime: 0,
  });
  return query.data ? gapWarningText(query.data) : null;
}

/** The warning box itself (the approved mock-up's yellow line), for screens that already hold the wording. */
export function GapWarningBox({ warning }: { warning: GapWarningText | null | undefined }) {
  if (!warning) return null;
  return (
    <output
      data-testid="gap-warning"
      className="flex items-start gap-2 rounded-md bg-semantic-warning-bg px-3 py-2 text-xs text-semantic-warning-text"
    >
      <TriangleAlert aria-hidden="true" size={14} className="mt-0.5 shrink-0" />
      <p className="m-0">
        <strong className="font-semibold">{warning.title}</strong> {warning.detail}
      </p>
    </output>
  );
}

/** Asks for the proposed window and shows the box when the gap is not kept. */
export function GapWarning(props: Parameters<typeof useGapWarning>[0]) {
  return <GapWarningBox warning={useGapWarning(props)} />;
}
